import {
  FormattedTaxRate,
  InvoiceData,
  InvoiceItemData,
  InvoiceTaxConfig,
  StoredTypeTaxAssignment,
  TaxRate,
  TypeTaxAssignment
} from "../../../../../types/invoices";
import {
  BookingV4,
  Item,
  Preorder,
  Refund
} from "../../../../../types/bookings";

/**
 * Round a number to a specified number of decimal places
 */
export const roundToDecimal = (value: number, decimals: number): number => {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
};

/**
 * Calculate the rate value (multiplier) from a tax rate.
 * The API stores tax rate values as direct multipliers (e.g. 1.2 for 20% tax).
 * Falls back to rate field, then defaults to 1 (no tax).
 */
export const getRateValue = (taxRate?: TaxRate): number => {
  if (!taxRate) return 1;
  const value = taxRate.value ?? taxRate.rate ?? 0;
  if (!value || value <= 0) return 1;
  return value;
};

/**
 * Calculate net amount for an invoice item
 */
export const calculateItemNet = (item: InvoiceItemData): number => {
  const n = parseFloat(String(item.net_amount ?? ""));
  const g = parseFloat(String(item.gross_amount ?? ""));
  const rateValue = getRateValue(item.tax_rate);

  if (!item.taxable || rateValue <= 1) {
    if (isFinite(n)) return n;
    return isFinite(g) ? g : 0;
  }

  if (item.tax_inclusive !== false) {
    return isFinite(g) ? g / rateValue : 0;
  }

  return isFinite(n) ? n : 0;
};

/**
 * Calculate gross amount for an invoice item
 */
export const calculateItemGross = (item: InvoiceItemData): number => {
  const g = parseFloat(String(item.gross_amount ?? ""));
  const n = parseFloat(String(item.net_amount ?? ""));
  const rateValue = getRateValue(item.tax_rate);

  if (!item.taxable || rateValue <= 1) {
    if (isFinite(g)) return g;
    return isFinite(n) ? n : 0;
  }

  if (item.tax_inclusive !== false) {
    return isFinite(g) ? g : 0;
  }

  return isFinite(n) ? n * rateValue : 0;
};

/**
 * Calculate tax amount for an invoice item
 */
export const calculateItemTax = (item: InvoiceItemData): number => {
  return calculateItemGross(item) - calculateItemNet(item);
};

/**
 * Calculate per-item multi-rate tax breakdown for display.
 * For items with multiple tax rates, individual taxes are calculated from the
 * net amount (derived using the combined rate) to ensure the sum of individual
 * taxes equals the total tax amount. Rounding adjustment is applied to the
 * last rate to prevent penny discrepancies.
 * Matches collins-ui's InvoiceItem.formattedTaxRates getter.
 */
export const getFormattedTaxRates = (
  item: InvoiceItemData
): FormattedTaxRate[] => {
  const gross = calculateItemGross(item);
  const rates = item.tax_rates ?? [];

  if (rates.length === 0) return [];

  const isTaxInclusive = item.tax_inclusive !== false;

  const combinedRate =
    rates.reduce((sum, r) => sum + (r.value ?? r.rate ?? 1), 0) -
    rates.length +
    1;

  const netAmount =
    isTaxInclusive && combinedRate > 1 ? gross / combinedRate : gross;

  const rawTax = isTaxInclusive
    ? gross - netAmount
    : netAmount * (combinedRate - 1);
  const totalTax = combinedRate > 1 ? roundToDecimal(rawTax, 2) : 0;

  const result: FormattedTaxRate[] = [];
  let runningTotal = 0;

  rates.forEach((r, index) => {
    const rv = r.value ?? r.rate ?? 0;
    let tax = rv > 1 ? netAmount * (rv - 1) : 0;

    if (index === rates.length - 1 && rates.length > 1) {
      tax = roundToDecimal(totalTax - runningTotal, 2);
    } else {
      tax = roundToDecimal(tax, 2);
      runningTotal += tax;
    }

    result.push({
      label: r.label ?? r.name ?? "",
      tax
    });
  });

  return result;
};

/**
 * Calculate total gross for all invoice items (excluding footer type)
 */
export const calculateInvoiceGross = (items: InvoiceItemData[]): number => {
  const total = items.reduce((sum, item) => {
    if (item.type === "footer") return sum;
    const itemGross = calculateItemGross(item);
    return sum + (isFinite(itemGross) ? itemGross : 0);
  }, 0);
  return roundToDecimal(total, 2);
};

/**
 * Calculate total tax for all invoice items (excluding footer type)
 */
export const calculateInvoiceTax = (items: InvoiceItemData[]): number => {
  const total = items.reduce((sum, item) => {
    if (item.type === "footer") return sum;
    const itemTax = calculateItemTax(item);
    return sum + (isFinite(itemTax) ? itemTax : 0);
  }, 0);
  return roundToDecimal(total, 2);
};

/**
 * Calculate total net for all invoice items
 */
export const calculateInvoiceNet = (items: InvoiceItemData[]): number => {
  return calculateInvoiceGross(items) - calculateInvoiceTax(items);
};

/**
 * Get tax breakdown by rate label
 */
export const getTaxBreakdown = (
  items: InvoiceItemData[]
): Record<string, number> => {
  const breakdowns: Record<string, number> = {};

  items.forEach((item) => {
    const taxAmount = calculateItemTax(item);
    if (!taxAmount || item.type === "heading" || item.type === "footer") {
      return;
    }

    if (item.multiple_rates && Array.isArray(item.formatted_tax_rates)) {
      item.formatted_tax_rates.forEach((taxRate) => {
        if (taxRate.label && taxRate.tax) {
          breakdowns[taxRate.label] =
            (breakdowns[taxRate.label] ?? 0) + taxRate.tax;
        }
      });
      return;
    }

    if (!item.tax_rate?.label) {
      return;
    }

    breakdowns[item.tax_rate.label] =
      (breakdowns[item.tax_rate.label] ?? 0) + taxAmount;
  });

  return breakdowns;
};

/**
 * Create a combined tax rate from multiple rates.
 * Uses the compounding formula from collins-ui:
 * totalRate = rates.reduce((sum, rate) => sum + rate.rate, 0) - rates.length + 1
 *
 * When taxLocking is false and multiple rates exist, returns only the first
 * non-zero rate instead of combining (matches collins-ui non-locking guard).
 */
export const createCombinedTaxRate = (
  taxRates: TaxRate[],
  taxLocking: boolean = true
): TaxRate => {
  if (taxRates.length === 0) {
    return { label: "No Tax", value: 0 };
  }

  if (taxRates.length === 1) {
    return taxRates[0];
  }

  // Non-locking guard: when tax locking is OFF and multiple rates exist,
  // return only the first non-zero rate (matches collins-ui)
  if (!taxLocking && taxRates.length > 1) {
    const nonZeroRates = taxRates.filter(
      (rate) => (rate.value ?? rate.rate ?? 0) !== 0
    );
    if (nonZeroRates.length > 0) {
      return {
        label: nonZeroRates[0].label ?? nonZeroRates[0].name ?? "Tax",
        value: nonZeroRates[0].value ?? nonZeroRates[0].rate ?? 0
      };
    }
  }

  const combinedValue =
    taxRates.reduce((sum, rate) => sum + (rate.value ?? rate.rate ?? 0), 0) -
    taxRates.length +
    1;

  const combinedLabel = taxRates
    .map((rate) => rate.label ?? rate.name)
    .join(" + ");

  return {
    label: combinedLabel,
    value: combinedValue
  };
};

/**
 * Build the invoice body object for saving to the API
 */
export const buildInvoiceBody = (invoice: InvoiceData): InvoiceData => {
  const items = (invoice.items ?? []).map((item) => ({
    description: item.description ?? "",
    net_amount: calculateItemNet(item),
    gross_amount: calculateItemGross(item),
    tax_amount: calculateItemTax(item),
    tax_rate: item.tax_rate,
    tax_rates: item.tax_rates,
    tax_inclusive: item.tax_inclusive,
    taxable: item.taxable,
    multiple_rates: item.multiple_rates,
    locked_tax: item.locked_tax,
    formatted_tax_rates: item.formatted_tax_rates,
    quantity: item.quantity,
    unit: item.unit,
    unit_price: item.unit_price,
    type: item.type,
    type_id: item.type_id,
    preorder_item_id: item.preorder_item_id
  }));

  return {
    ...invoice,
    items
  };
};

/**
 * Create a blank invoice item with default values
 */
export const createBlankInvoiceItem = (
  defaultTaxRate?: TaxRate
): InvoiceItemData => ({
  uid: crypto.randomUUID(),
  description: "",
  gross_amount: 0,
  tax_amount: 0,
  taxable: false,
  tax_rate: defaultTaxRate
});

/**
 * Format a number as currency display (2 decimal places)
 */
export const formatDecimal = (value: number): string => {
  if (!isFinite(value)) return "0.00";
  return value.toFixed(2);
};

/**
 * Check if venue company information is complete for invoicing
 */
export const canManageInvoices = (companyInformation?: {
  company_name?: string;
  registered_address?: string;
  vat_number?: string;
}): boolean => {
  if (!companyInformation) return false;

  const requiredFields: (keyof typeof companyInformation)[] = [
    "company_name",
    "registered_address",
    "vat_number"
  ];

  return requiredFields.every(
    (field) =>
      companyInformation[field] !== undefined && !!companyInformation[field]
  );
};

/**
 * Resolve the invoice label for a given type.
 * Checks venue-level booking_invoicing overrides first,
 * then falls back to the type's invoice_label from typesCache,
 * then to the type name, then to the default label.
 * Matches collins-ui's getInvoiceLabelForType.
 *
 * @param labelKey - The key to look up in venue's booking_invoicing
 * @param defaultLabel - The default label to use if no override found
 * @param venueConfig - Venue-level invoicing overrides
 * @param typeId - Optional type ID to look up in typesCache
 * @param typesCache - Optional array of preorder types with invoice_label
 */
export const getInvoiceLabelForType = (
  labelKey: string,
  defaultLabel: string,
  venueConfig?: {
    override_booking_invoicing?: boolean;
    booking_invoicing?: Record<string, string> | null;
  },
  typeId?: string | null,
  typesCache?: Array<{ id: string; name: string; invoice_label?: string }>
): string => {
  // Check venue-level override first
  if (
    venueConfig?.override_booking_invoicing &&
    venueConfig.booking_invoicing?.[labelKey]
  ) {
    return venueConfig.booking_invoicing[labelKey];
  }

  // Fall back to type's invoice_label from typesCache
  if (typesCache && typeId) {
    const typeEntry = typesCache.find((t) => t.id === typeId);
    if (typeEntry) {
      return typeEntry.invoice_label ?? typeEntry.name ?? defaultLabel;
    }
  }

  return defaultLabel;
};

/**
 * Group deposits by their tax rate IDs and invoice label.
 * Returns a map keyed by sorted tax_rate_ids joined with invoice_label.
 */
export const groupDepositsByTaxRateIdsAndLabel = (
  deposits: Array<{
    amount: number;
    tax_rate_ids?: string[];
    invoice_label?: string;
    source?: string;
  }>
): Map<
  string,
  {
    totalAmount: number;
    label: string;
    tax_rate_ids: string[];
    deposits: typeof deposits;
  }
> => {
  const groups = new Map<
    string,
    {
      totalAmount: number;
      label: string;
      tax_rate_ids: string[];
      deposits: typeof deposits;
    }
  >();

  const sourceOrder: Record<string, number> = {
    preorder: 0,
    payment: 1,
    booking_type: 2
  };

  const sortedDeposits = [...deposits].sort((a, b) => {
    const orderA = sourceOrder[a.source ?? ""] ?? 3;
    const orderB = sourceOrder[b.source ?? ""] ?? 3;
    return orderA - orderB;
  });

  for (const deposit of sortedDeposits) {
    const sortedIds = [...(deposit.tax_rate_ids ?? [])].sort((a, b) =>
      a.localeCompare(b)
    );
    const key = `${sortedIds.join(",")}_${deposit.invoice_label ?? ""}`;

    const existing = groups.get(key);
    if (existing) {
      existing.totalAmount += deposit.amount;
      existing.deposits.push(deposit);
    } else {
      groups.set(key, {
        totalAmount: deposit.amount,
        label: deposit.invoice_label ?? "Deposit",
        tax_rate_ids: sortedIds,
        deposits: [deposit]
      });
    }
  }

  return groups;
};

/**
 * Calculate the total preorder amount from all preorders on a booking.
 * Used to deduct preorder totals from deposit amounts before invoicing,
 * matching collins-ui's applyDepositSourceOrdering logic.
 */
export const calculatePreorderTotal = (preorders: Preorder[]): number => {
  return preorders.reduce((sum, preorder) => {
    const cost =
      typeof preorder.total_cost === "number"
        ? preorder.total_cost
        : parseFloat(String(preorder.total_cost)) || 0;
    return sum + cost;
  }, 0);
};

/**
 * Apply preorder deduction to deposit amounts.
 * Subtracts the total preorder cost from deposits sourced from preorders
 * before grouping for invoice line items. This prevents double-counting
 * when preorder items are also added as separate invoice line items.
 * Matches collins-ui's applyDepositSourceOrdering behaviour.
 */
export const applyPreorderDeduction = (
  deposits: Array<{
    amount: number;
    tax_rate_ids?: string[];
    invoice_label?: string;
    source?: string;
  }>,
  preorderTotal: number
): Array<{
  amount: number;
  tax_rate_ids?: string[];
  invoice_label?: string;
  source?: string;
}> => {
  let remaining = preorderTotal;

  return deposits
    .map((deposit) => {
      if (deposit.source === "preorder" && remaining > 0) {
        const deduction = Math.min(deposit.amount, remaining);
        remaining -= deduction;
        return { ...deposit, amount: deposit.amount - deduction };
      }
      return deposit;
    })
    .filter((deposit) => deposit.amount > 0);
};

/**
 * Deposit types that should be excluded from refund grouping.
 * Matches collins-ui's getRefundsGroupedByDeposit preorderDeposits filter.
 */
const PREORDER_DEPOSIT_TYPES = ["customer_preorder", "request_auth"];

/**
 * Calculate the total refund amount for a single deposit.
 * Sums refund amounts by looking up deposit.refunds (or refund_ids)
 * in the provided refund reference map.
 */
const calculateDepositRefundTotal = (
  deposit: {
    refund_ids?: string[] | null;
    refunds?: string[] | null;
  },
  refundsByRef: Map<string, Refund>
): number => {
  const refs = deposit.refunds ?? deposit.refund_ids ?? [];
  if (!Array.isArray(refs)) return 0;

  let total = 0;
  for (const refundRef of refs) {
    const refund = refundsByRef.get(refundRef);
    if (!refund) continue;
    const rawAmount =
      typeof refund.amount === "number"
        ? refund.amount
        : ((refund.amount as { amount?: number })?.amount ?? 0);
    if (rawAmount > 0) {
      total += rawAmount;
    }
  }
  return total;
};

/**
 * Group refunds by their associated deposit's tax rate IDs and invoice label.
 * Returns entries suitable for creating negative-amount invoice line items.
 *
 * Mirrors the two-step approach from collins-ui:
 * 1. getRefundsGroupedByDeposit – iterates deposits, looks up deposit.refunds
 *    (refund_ref strings), then cross-references booking.refunds to get actual
 *    refund amounts. Skips preorder deposit types.
 * 2. groupRefundsByTaxRateIdsAndLabel – groups by deposit tax_rate_ids + label.
 */
export const groupRefundsByTaxRateIdsAndLabel = (
  refunds: Refund[],
  deposits: Array<{
    amount: number;
    tax_rate_ids?: string[];
    invoice_label?: string;
    source?: string;
    id?: string;
    refund_ids?: string[] | null;
    refunds?: string[] | null;
    type?: string;
    notes?: string;
  }>
): Map<
  string,
  {
    totalAmount: number;
    label: string;
    tax_rate_ids: string[];
  }
> => {
  const refundsByRef = new Map(refunds.map((r) => [r.refund_ref, r]));

  const groups = new Map<
    string,
    {
      totalAmount: number;
      label: string;
      tax_rate_ids: string[];
    }
  >();

  for (const deposit of deposits) {
    if (deposit.type && PREORDER_DEPOSIT_TYPES.includes(deposit.type)) {
      continue;
    }

    const refundAmount = calculateDepositRefundTotal(deposit, refundsByRef);
    if (refundAmount <= 0) continue;

    const taxRateIds = deposit.tax_rate_ids ?? [];
    const label = deposit.invoice_label ?? "Deposit";
    const sortedIds = [...taxRateIds].sort((a, b) => a.localeCompare(b));
    const key = `${sortedIds.join(",")}_${label}`;

    const existing = groups.get(key);
    if (existing) {
      existing.totalAmount += refundAmount;
    } else {
      groups.set(key, {
        totalAmount: refundAmount,
        label,
        tax_rate_ids: sortedIds
      });
    }
  }

  return groups;
};

/**
 * Remove orphaned structural items (headings/footers) when the last
 * regular item of a type group is removed.
 * Matches collins-ui's removeStructuralItems logic.
 */
export const removeOrphanedStructuralItems = (
  items: InvoiceItemData[]
): InvoiceItemData[] => {
  // Collect type_ids that still have at least one regular item
  const activeTypeIds = new Set<string>();
  for (const item of items) {
    if (item.type !== "heading" && item.type !== "footer" && item.type_id) {
      activeTypeIds.add(item.type_id);
    }
  }

  // Keep structural items only if their type_id still has regular items,
  // or if they have no type_id (standalone heading/footer)
  return items.filter((item) => {
    if (item.type === "heading" || item.type === "footer") {
      return !item.type_id || activeTypeIds.has(item.type_id);
    }
    return true;
  });
};

/**
 * Find the indices of an existing heading and footer for a given type group.
 * Used when re-running "Add from Pre-orders" on an invoice that already
 * contains items — new items should be inserted between the existing
 * heading and footer rather than appended to the end.
 * Matches collins-ui's findExistingHeaderAndFooterIndices.
 */
export const findExistingHeaderAndFooterIndices = (
  items: InvoiceItemData[],
  typeId: string
): { headerIndex: number; footerIndex: number } => {
  let headerIndex = -1;
  let footerIndex = -1;

  items.some((item, index) => {
    if (item.type_id === typeId) {
      if (item.type === "heading") {
        headerIndex = index;
      } else if (item.type === "footer") {
        footerIndex = index;
      }
    }
    return headerIndex !== -1 && footerIndex !== -1;
  });

  return { headerIndex, footerIndex };
};

// ---------------------------------------------------------------------------
// Preorder helpers
// ---------------------------------------------------------------------------

/**
 * Get all active (non-deleted) preorders from a booking.
 * Matches collins-ui's BookingsManager.getActivePreorders.
 */
export const getActivePreorders = (booking: BookingV4): Preorder[] => {
  if (!Array.isArray(booking.preorders)) return [];
  return booking.preorders.filter(
    (order) => !order.status || order.status !== "deleted"
  );
};

/**
 * Flatten all items from active preorders into a single array.
 * Each item receives the parent preorder's id as `order_id`.
 * Matches collins-ui's BookingsManager.getAllPreorderItems.
 */
export const getAllPreorderItems = (
  booking: BookingV4
): (Item & { order_id: string })[] => {
  const items: (Item & { order_id: string })[] = [];

  for (const preorder of getActivePreorders(booking)) {
    for (const item of preorder.items ?? []) {
      items.push({ ...item, order_id: preorder.id });
    }
  }

  return items;
};

/**
 * Sum of all deposit amounts on a booking (handles nested Amount objects).
 * Matches collins-ui's BookingsManager.getBookingDepositTotal.
 */
export const getBookingDepositTotal = (booking: BookingV4): number => {
  if (!Array.isArray(booking.deposits)) return 0;
  return booking.deposits.reduce((sum, deposit) => {
    const raw = deposit.amount;
    const amount =
      typeof raw === "number"
        ? raw
        : ((raw as { amount?: number })?.amount ?? 0);
    return sum + amount;
  }, 0);
};

/**
 * Sum of service_charge_amount across all active preorders.
 * Matches collins-ui's BookingsManager.getBookingServiceChargeTotal.
 */
export const getBookingServiceChargeTotal = (booking: BookingV4): number => {
  return getActivePreorders(booking).reduce(
    (sum, order) => sum + (order.service_charge_amount ?? 0),
    0
  );
};

// ---------------------------------------------------------------------------
// Type-tax-assignment helpers
// ---------------------------------------------------------------------------

/**
 * Retrieve the stored type-tax-assignment from the booking's invoice_tax_config.
 * Returns null when no stored assignment exists for the given type.
 * Matches collins-ui's getStoredTypeTaxAssignment.
 */
export const getStoredTypeTaxAssignment = (
  typeId: string,
  invoiceTaxConfig?: InvoiceTaxConfig
): StoredTypeTaxAssignment | null => {
  if (!typeId || !invoiceTaxConfig?.types) return null;
  return invoiceTaxConfig.types[typeId] ?? null;
};

/**
 * Resolve the applicable tax rates for a given type.
 * Checks stored rates on the booking first (locked from first invoice),
 * then falls back to current typeTaxAssignments from the API.
 * Matches collins-ui's getTaxRatesForType.
 */
export const getTaxRatesForType = (
  typeId: string,
  invoiceTaxConfig?: InvoiceTaxConfig,
  typeTaxAssignments?: TypeTaxAssignment[]
): TaxRate[] => {
  if (!typeId) return [];

  // First: stored rates from booking (locked in from first invoice)
  const stored = getStoredTypeTaxAssignment(typeId, invoiceTaxConfig);
  if (
    stored?.resolved_tax_rates &&
    Array.isArray(stored.resolved_tax_rates) &&
    stored.resolved_tax_rates.length > 0
  ) {
    return stored.resolved_tax_rates;
  }

  // Fallback: current rates from typeTaxAssignments
  if (!Array.isArray(typeTaxAssignments)) return [];
  const assignment = typeTaxAssignments.find((a) => a.type_id === typeId);
  return assignment?.tax_rates ?? [];
};

/**
 * Resolve the tax_inclusive flag for a given type.
 * Checks stored setting first, then falls back to typeTaxAssignments.
 * Matches collins-ui's getTaxInclusiveForType.
 */
export const getTaxInclusiveForType = (
  typeId: string,
  invoiceTaxConfig?: InvoiceTaxConfig,
  typeTaxAssignments?: TypeTaxAssignment[]
): boolean => {
  if (!typeId) return true;

  const stored = getStoredTypeTaxAssignment(typeId, invoiceTaxConfig);
  if (stored && typeof stored.tax_inclusive === "boolean") {
    return stored.tax_inclusive;
  }

  if (!Array.isArray(typeTaxAssignments)) return true;
  const assignment = typeTaxAssignments.find((a) => a.type_id === typeId);
  return typeof assignment?.tax_inclusive === "boolean"
    ? assignment.tax_inclusive
    : true;
};

// ---------------------------------------------------------------------------
// Process preorder items
// ---------------------------------------------------------------------------

interface TypeGroup {
  items: (Item & { order_id: string })[];
  itemAmounts: Record<string, number>;
  itemUnitPrices: Record<string, number>;
  totalAmount: number;
  typeId: string;
  serviceCharge: number;
}

interface UncategorisedBucket {
  amount: number;
  taxable: boolean;
  label: string;
}

interface IndividualUncategorisedItem {
  item: Item & { order_id: string };
  amount: number;
  serviceCharge: number;
  itemType: string;
}

interface ProcessedPreorderItems {
  itemsByType: Record<string, TypeGroup>;
  uncategorisedItems: Record<string, UncategorisedBucket>;
  individualUncategorisedItems: IndividualUncategorisedItem[];
}

/**
 * Resolve the type_id for a preorder item.
 * Prefers the item-level type_id, then looks up from venue packages.
 */
const resolveItemTypeId = (
  item: Item & { order_id: string },
  venuePackages?: Array<{ id: string; type_id?: string }>
): string | undefined => {
  if (typeof item.type_id === "string") return item.type_id;
  if (!venuePackages) return undefined;
  const lookupId = item.preorder_item_id ?? item.id;
  return venuePackages.find((p) => p.id === lookupId)?.type_id;
};

/**
 * Calculate the amount and unit price for a preorder item.
 * Menu items use discount-proportion logic; non-menu items use base_price.
 */
const calculatePreorderItemAmountAndPrice = (
  item: Item & { order_id: string },
  venuePackages?: Array<{ id: string; price?: number; base_price?: number }>
): { amount: number; unitPrice: number } => {
  if (item.type === "menu" && Array.isArray(item.items)) {
    const nonDiscountedPrice = item.items.reduce((total, menuItem) => {
      const pkg = venuePackages?.find((p) => p.id === menuItem.id);
      return total + (pkg?.price ?? 0);
    }, 0);

    if (!nonDiscountedPrice) {
      return { amount: item.total ?? 0, unitPrice: item.base_price ?? 0 };
    }

    const discountPercentage = (item.total ?? 0) / nonDiscountedPrice;
    let amount = 0;
    let unitPrice = 0;
    for (const menuItem of item.items) {
      const pkg = venuePackages?.find((p) => p.id === menuItem.id);
      amount += discountPercentage * (pkg?.price ?? 0);
      unitPrice += discountPercentage * (pkg?.base_price ?? 0);
    }
    return { amount, unitPrice };
  }

  return {
    amount: item.total ?? 0,
    unitPrice: item.base_price ?? (item.total ?? 0) / (item.quantity || 1)
  };
};

/**
 * Distribute a discount proportionally across items in a type group.
 */
const applyDiscountToTypeGroup = (
  typeGroup: TypeGroup,
  discountAmount: number
): void => {
  const totalBefore = typeGroup.items.reduce(
    (sum, item) => sum + (typeGroup.itemAmounts[item.id] ?? 0),
    0
  );
  if (totalBefore <= 0) return;

  for (const item of typeGroup.items) {
    const current = typeGroup.itemAmounts[item.id] ?? 0;
    const proportion = current / totalBefore;
    typeGroup.itemAmounts[item.id] = current - discountAmount * proportion;
  }
};

/**
 * Distribute a discount proportionally across uncategorised items.
 */
const applyDiscountToUncategorised = (
  bucket: UncategorisedBucket,
  items: IndividualUncategorisedItem[],
  discountAmount: number
): void => {
  bucket.amount -= discountAmount;
  const totalAmount = items.reduce((sum, i) => sum + i.amount, 0);
  if (totalAmount <= 0) return;

  for (const item of items) {
    const proportion = item.amount / totalAmount;
    item.amount -= discountAmount * proportion;
  }
};

/**
 * Apply order-level discounts from all active preorders.
 * Discounts are distributed proportionally to type groups or uncategorised buckets.
 */
const applyOrderDiscounts = (
  booking: BookingV4,
  itemsByType: Record<string, TypeGroup>,
  uncategorisedItems: Record<string, UncategorisedBucket>,
  individualUncategorisedItems: IndividualUncategorisedItem[],
  venuePackages?: Array<{ id: string; type_id?: string }>
): void => {
  const activePreorders = getActivePreorders(booking);
  for (const preorder of activePreorders) {
    if (!Array.isArray(preorder.order_discounts)) continue;

    for (const discount of preorder.order_discounts) {
      const discountTypeId = discount.item_id
        ? venuePackages?.find((p) => p.id === discount.item_id)?.type_id
        : undefined;

      if (discountTypeId && itemsByType[discountTypeId]) {
        applyDiscountToTypeGroup(itemsByType[discountTypeId], discount.amount);
      } else {
        const targetType = "other";
        const bucket =
          uncategorisedItems[targetType] ?? uncategorisedItems.other;
        const targetItems = individualUncategorisedItems.filter(
          (i) => i.itemType === targetType
        );
        applyDiscountToUncategorised(bucket, targetItems, discount.amount);
      }
    }
  }
};

/**
 * Group preorder items by type, calculate per-item amounts and service charges.
 * Items with a type_id are grouped into `itemsByType`; those without fall into
 * uncategorised buckets by their `type` field (food / drink / other).
 *
 * Matches collins-ui's processPreorderItems.
 */
export const processPreorderItems = (
  booking: BookingV4,
  venuePackages?: Array<{
    id: string;
    type_id?: string;
    price?: number;
    base_price?: number;
  }>
): ProcessedPreorderItems => {
  const preorderItems = getAllPreorderItems(booking);
  const itemsByType: Record<string, TypeGroup> = {};
  const uncategorisedItems: Record<string, UncategorisedBucket> = {
    food: { amount: 0, taxable: true, label: "Pre-ordered Food" },
    drink: { amount: 0, taxable: true, label: "Pre-ordered Drinks" },
    other: { amount: 0, taxable: true, label: "Other pre-ordered items" }
  };
  const individualUncategorisedItems: IndividualUncategorisedItem[] = [];

  const bookingServiceChargePercent = booking.service_charge;
  const addServiceCharge = booking.add_service_charge;

  for (const currentItem of preorderItems) {
    const typeId = resolveItemTypeId(currentItem, venuePackages);
    const { amount: itemAmount, unitPrice: itemUnitPrice } =
      calculatePreorderItemAmountAndPrice(currentItem, venuePackages);

    let serviceCharge = 0;
    if (bookingServiceChargePercent && addServiceCharge) {
      serviceCharge = itemAmount * bookingServiceChargePercent * 0.01;
    }

    if (typeId) {
      if (!itemsByType[typeId]) {
        itemsByType[typeId] = {
          items: [],
          itemAmounts: {},
          itemUnitPrices: {},
          totalAmount: 0,
          typeId,
          serviceCharge: 0
        };
      }
      const group = itemsByType[typeId];
      group.items.push(currentItem);
      group.itemAmounts[currentItem.id] = itemAmount;
      group.itemUnitPrices[currentItem.id] = itemUnitPrice;
      group.totalAmount += itemAmount;
      group.serviceCharge += serviceCharge;
    } else {
      const itemType = currentItem.type || "other";
      const bucket = uncategorisedItems[itemType] ?? uncategorisedItems.other;
      bucket.amount += itemAmount;
      individualUncategorisedItems.push({
        item: currentItem,
        amount: itemAmount,
        serviceCharge,
        itemType
      });
    }
  }

  applyOrderDiscounts(
    booking,
    itemsByType,
    uncategorisedItems,
    individualUncategorisedItems,
    venuePackages
  );

  // Recalculate totalAmount after discount distribution
  for (const typeGroup of Object.values(itemsByType)) {
    typeGroup.totalAmount = typeGroup.items.reduce(
      (sum, item) => sum + (typeGroup.itemAmounts[item.id] ?? 0),
      0
    );
  }

  return { itemsByType, uncategorisedItems, individualUncategorisedItems };
};

// ---------------------------------------------------------------------------
// Build preorder invoice items
// ---------------------------------------------------------------------------

interface PreorderInvoiceConfig {
  booking: BookingV4;
  invoiceTaxConfig?: InvoiceTaxConfig;
  typeTaxAssignments?: TypeTaxAssignment[];
  taxRates: TaxRate[];
  taxLocking: boolean;
  individualItemsEnabled: boolean;
  venuePackages?: Array<{
    id: string;
    type_id?: string;
    name?: string;
    price?: number;
    base_price?: number;
  }>;
  typesCache?: Array<{ id: string; name: string }>;
  venueConfig?: {
    override_booking_invoicing?: boolean;
    booking_invoicing?: Record<string, string> | null;
  };
}

/**
 * Build individual heading/item/footer invoice items for a single type group.
 */
const buildIndividualTypeGroupItems = (
  typeGroup: TypeGroup,
  typeName: string,
  combinedRate: TaxRate | undefined,
  typeRates: TaxRate[],
  taxInclusive: boolean,
  taxLocking: boolean
): InvoiceItemData[] => {
  const items: InvoiceItemData[] = [];

  items.push({
    uid: crypto.randomUUID(),
    description: typeName,
    type: "heading",
    type_id: typeGroup.typeId
  });

  for (const item of typeGroup.items) {
    const finalAmount = typeGroup.itemAmounts[item.id] ?? 0;
    if (finalAmount <= 0) continue;

    const amount = roundToDecimal(finalAmount, 2);
    items.push({
      uid: crypto.randomUUID(),
      description: item.name,
      taxable: true,
      tax_rate: combinedRate,
      tax_rates: typeRates.length > 1 ? typeRates : undefined,
      multiple_rates: typeRates.length > 1,
      locked_tax: taxLocking && typeRates.length > 0,
      tax_inclusive: taxInclusive,
      type: "item",
      type_id: typeGroup.typeId,
      quantity: item.quantity || 1,
      unit: typeGroup.itemUnitPrices[item.id],
      preorder_item_id: item.id,
      ...(taxInclusive ? { gross_amount: amount } : { net_amount: amount })
    });
  }

  const subtotal = roundToDecimal(typeGroup.totalAmount, 2);
  items.push({
    uid: crypto.randomUUID(),
    description: `Subtotal ${typeName}`,
    taxable: true,
    tax_rate: combinedRate,
    tax_inclusive: taxInclusive,
    type: "footer",
    type_id: typeGroup.typeId,
    ...(taxInclusive ? { gross_amount: subtotal } : { net_amount: subtotal })
  });

  return items;
};

interface AggregatedTypeGroupItemConfig {
  typeGroup: TypeGroup;
  typeName: string;
  combinedRate: TaxRate | undefined;
  typeRates: TaxRate[];
  taxRates: TaxRate[];
  taxInclusive: boolean;
  taxLocking: boolean;
  venueConfig?: PreorderInvoiceConfig["venueConfig"];
  typesCache?: PreorderInvoiceConfig["typesCache"];
}

/**
 * Build a single aggregated invoice item for a type group.
 */
const buildAggregatedTypeGroupItem = (
  config: AggregatedTypeGroupItemConfig
): InvoiceItemData => {
  const {
    typeGroup,
    typeName,
    combinedRate,
    typeRates,
    taxRates,
    taxInclusive,
    taxLocking,
    venueConfig,
    typesCache
  } = config;
  const amount = roundToDecimal(typeGroup.totalAmount, 2);
  const label = getInvoiceLabelForType(
    "preorder_label",
    `Pre-ordered ${typeName}`,
    venueConfig,
    typeGroup.typeId,
    typesCache
  );

  return {
    uid: crypto.randomUUID(),
    description: label,
    taxable: combinedRate !== undefined,
    tax_rate: combinedRate ?? (taxRates.length > 0 ? taxRates[0] : undefined),
    locked_tax: taxLocking && typeRates.length > 0,
    tax_inclusive: taxInclusive,
    ...(taxInclusive ? { gross_amount: amount } : { net_amount: amount })
  };
};

/**
 * Build invoice items for uncategorised preorder items (individual or aggregated).
 * Returns the items and the total service charge from uncategorised items.
 */
/**
 * Build individual uncategorised invoice items (one line per preorder item).
 */
const buildIndividualUncategorisedItems = (
  individualUncategorisedItems: IndividualUncategorisedItem[],
  taxRates: TaxRate[],
  taxLocking: boolean
): { items: InvoiceItemData[]; serviceCharge: number } => {
  const items: InvoiceItemData[] = [];
  let serviceCharge = 0;
  const defaultRate = taxRates.length > 0 ? taxRates[0] : undefined;

  for (const uncatItem of individualUncategorisedItems) {
    if (uncatItem.amount <= 0) continue;
    serviceCharge += uncatItem.serviceCharge;
    items.push({
      uid: crypto.randomUUID(),
      description: uncatItem.item.name || "Other pre-ordered items",
      gross_amount: roundToDecimal(uncatItem.amount, 2),
      taxable: true,
      tax_rate: defaultRate,
      locked_tax: taxLocking && taxRates.length === 1,
      quantity: uncatItem.item.quantity || 1,
      preorder_item_id: uncatItem.item.id
    });
  }

  return { items, serviceCharge };
};

/**
 * Build aggregated uncategorised invoice items (one line per bucket).
 */
const buildAggregatedUncategorisedItems = (
  uncategorisedItems: Record<string, UncategorisedBucket>,
  individualUncategorisedItems: IndividualUncategorisedItem[],
  taxRates: TaxRate[],
  taxLocking: boolean
): { items: InvoiceItemData[]; serviceCharge: number } => {
  const items: InvoiceItemData[] = [];
  const defaultRate = taxRates.length > 0 ? taxRates[0] : undefined;

  for (const bucket of Object.values(uncategorisedItems)) {
    if (bucket.amount <= 0) continue;
    items.push({
      uid: crypto.randomUUID(),
      description: bucket.label,
      gross_amount: roundToDecimal(bucket.amount, 2),
      taxable: bucket.taxable,
      tax_rate: defaultRate,
      locked_tax: bucket.taxable && taxLocking && taxRates.length === 1
    });
  }

  const serviceCharge = individualUncategorisedItems.reduce(
    (sum, item) => sum + (item.serviceCharge ?? 0),
    0
  );

  return { items, serviceCharge };
};

const buildUncategorisedInvoiceItems = (
  useIndividualItems: boolean,
  uncategorisedItems: Record<string, UncategorisedBucket>,
  individualUncategorisedItems: IndividualUncategorisedItem[],
  taxRates: TaxRate[],
  taxLocking: boolean
): { items: InvoiceItemData[]; serviceCharge: number } => {
  if (useIndividualItems) {
    return buildIndividualUncategorisedItems(
      individualUncategorisedItems,
      taxRates,
      taxLocking
    );
  }

  return buildAggregatedUncategorisedItems(
    uncategorisedItems,
    individualUncategorisedItems,
    taxRates,
    taxLocking
  );
};

/**
 * Build all preorder-related invoice items (preorder items + service charge).
 * When `individualItemsEnabled` is true AND type-tax-assignments are available,
 * creates heading/item/footer structure per type group.
 * Otherwise falls back to simple "Pre-ordered TypeName" aggregated items.
 *
 * Matches collins-ui's _addPreordersInvoiceItemsSync + addPreordersInvoiceItemsManual.
 */
export const buildPreorderInvoiceItems = (
  config: PreorderInvoiceConfig
): InvoiceItemData[] => {
  const {
    booking,
    invoiceTaxConfig,
    typeTaxAssignments,
    taxRates,
    taxLocking,
    individualItemsEnabled,
    venuePackages,
    typesCache,
    venueConfig
  } = config;

  const hasAssignments =
    Array.isArray(typeTaxAssignments) && typeTaxAssignments.length > 0;
  const useIndividualItems = individualItemsEnabled && hasAssignments;

  const { itemsByType, uncategorisedItems, individualUncategorisedItems } =
    processPreorderItems(booking, venuePackages);

  const newItems: InvoiceItemData[] = [];
  let bookingServiceCharge = 0;

  for (const typeGroup of Object.values(itemsByType)) {
    bookingServiceCharge += typeGroup.serviceCharge;
    if (typeGroup.totalAmount <= 0) continue;

    const typeRates = getTaxRatesForType(
      typeGroup.typeId,
      invoiceTaxConfig,
      typeTaxAssignments
    );
    const combinedRate =
      typeRates.length > 0
        ? createCombinedTaxRate(typeRates, taxLocking)
        : undefined;
    const taxInclusive = getTaxInclusiveForType(
      typeGroup.typeId,
      invoiceTaxConfig,
      typeTaxAssignments
    );

    const typeEntry = typesCache?.find((t) => t.id === typeGroup.typeId);
    const typeName = typeEntry?.name
      ? typeEntry.name.charAt(0).toUpperCase() + typeEntry.name.slice(1)
      : "Other";

    if (useIndividualItems) {
      newItems.push(
        ...buildIndividualTypeGroupItems(
          typeGroup,
          typeName,
          combinedRate,
          typeRates,
          taxInclusive,
          taxLocking
        )
      );
    } else {
      newItems.push(
        buildAggregatedTypeGroupItem({
          typeGroup,
          typeName,
          combinedRate,
          typeRates,
          taxRates,
          taxInclusive,
          taxLocking,
          venueConfig,
          typesCache
        })
      );
    }
  }

  const uncategorised = buildUncategorisedInvoiceItems(
    useIndividualItems,
    uncategorisedItems,
    individualUncategorisedItems,
    taxRates,
    taxLocking
  );
  newItems.push(...uncategorised.items);
  bookingServiceCharge += uncategorised.serviceCharge;

  const serviceChargeItem = createServiceChargeInvoiceItem({
    booking,
    bookingServiceCharge,
    invoiceTaxConfig,
    typeTaxAssignments,
    taxRates,
    taxLocking,
    typesCache,
    venueConfig
  });
  if (serviceChargeItem) {
    newItems.push(serviceChargeItem);
  }

  return newItems;
};

// ---------------------------------------------------------------------------
// Type ID helpers
// ---------------------------------------------------------------------------

/**
 * Find the type ID for "deposits" from the types cache.
 * Matches collins-ui's getDepositsTypeId.
 */
export const getDepositsTypeId = (
  typesCache?: Array<{ id: string; name: string }>
): string | null => {
  if (!Array.isArray(typesCache)) return null;
  const found = typesCache.find(
    (t) => typeof t.name === "string" && t.name.toLowerCase() === "deposits"
  );
  return found?.id ?? null;
};

/**
 * Find the type ID for "service charge" from the types cache.
 * Matches collins-ui's getServiceChargeTypeId.
 */
export const getServiceChargeTypeId = (
  typesCache?: Array<{ id: string; name: string }>
): string | null => {
  if (!Array.isArray(typesCache)) return null;
  const found = typesCache.find(
    (t) =>
      typeof t.name === "string" && t.name.toLowerCase() === "service charge"
  );
  return found?.id ?? null;
};

interface ServiceChargeConfig {
  booking: BookingV4;
  bookingServiceCharge: number;
  invoiceTaxConfig?: InvoiceTaxConfig;
  typeTaxAssignments?: TypeTaxAssignment[];
  taxRates?: TaxRate[];
  taxLocking?: boolean;
  typesCache?: Array<{ id: string; name: string }>;
  venueConfig?: {
    override_booking_invoicing?: boolean;
    booking_invoicing?: Record<string, string> | null;
  };
}

/**
 * Create a service charge invoice line item.
 * Combines pre-calculated service charges from preorders with
 * the booking-level service charge total.
 * Matches collins-ui's addGlobalServiceChargeItem.
 */
export const createServiceChargeInvoiceItem = (
  config: ServiceChargeConfig
): InvoiceItemData | null => {
  const {
    booking,
    bookingServiceCharge,
    invoiceTaxConfig,
    typeTaxAssignments,
    taxRates,
    taxLocking,
    typesCache,
    venueConfig
  } = config;

  const preCalculatedServiceCharge = getActivePreorders(booking).reduce(
    (total, preorder) => total + (preorder.service_charge_amount ?? 0),
    0
  );
  const totalServiceCharge = preCalculatedServiceCharge + bookingServiceCharge;

  if (totalServiceCharge <= 0) return null;

  const serviceChargeTypeId = getServiceChargeTypeId(typesCache);
  const label = getInvoiceLabelForType(
    "service_charge_label",
    "Service charge",
    venueConfig,
    serviceChargeTypeId,
    typesCache
  );

  const typeRates = serviceChargeTypeId
    ? getTaxRatesForType(
        serviceChargeTypeId,
        invoiceTaxConfig,
        typeTaxAssignments
      )
    : [];
  const hasTaxAssignment = Array.isArray(typeRates) && typeRates.length > 0;
  const fallbackRate =
    taxRates && taxRates.length > 0 ? taxRates[0] : undefined;
  const rate = hasTaxAssignment
    ? createCombinedTaxRate(typeRates, !!taxLocking)
    : fallbackRate;
  const taxInclusive =
    hasTaxAssignment && serviceChargeTypeId
      ? getTaxInclusiveForType(
          serviceChargeTypeId,
          invoiceTaxConfig,
          typeTaxAssignments
        )
      : true;

  const amount = roundToDecimal(totalServiceCharge, 2);

  if (hasTaxAssignment) {
    return {
      uid: crypto.randomUUID(),
      description: label,
      taxable: true,
      tax_rate: rate,
      tax_rates: typeRates.length > 1 ? typeRates : undefined,
      multiple_rates: typeRates.length > 1,
      locked_tax: !!taxLocking && typeRates.length > 0,
      tax_inclusive: taxInclusive,
      ...(taxInclusive ? { gross_amount: amount } : { net_amount: amount })
    };
  }

  return {
    uid: crypto.randomUUID(),
    description: label,
    gross_amount: amount,
    taxable: false,
    tax_rate: rate
  };
};

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

/**
 * Mirrors API shouldProcessRefundsAsItems():
 * Refunds should be shown as separate invoice items when:
 * 1. No active preorder items on the booking (deposit-only)
 * 2. Total payments net to 0 (all deposits were fully refunded)
 * 3. Invoices already exist (payment was invoiced before the refund)
 *
 * Matches collins-ui's shouldProcessRefundsAsItems.
 */
export const shouldProcessRefundsAsItems = (booking: BookingV4): boolean => {
  // Check if booking has any active preorder items
  const preorderItems = getAllPreorderItems(booking);
  if (preorderItems.length > 0) return false;

  // Check if total payments is effectively 0 (all deposits refunded)
  // Use threshold to avoid floating-point precision issues
  const paidDepositsTotal = getBookingDepositTotal(booking);
  if (Math.abs(paidDepositsTotal) >= 0.01) return false;

  // Check if invoices already exist on the booking
  if (!booking.invoices?.length) return false;

  return true;
};

/**
 * Check whether the booking has enough data to produce invoice items.
 * Used to decide if API generation should be attempted.
 * Matches collins-ui's wouldInvoiceHaveItems.
 */
export const wouldInvoiceHaveItems = (booking: BookingV4): boolean => {
  const preorderItems = getAllPreorderItems(booking);
  let preorderTotal = 0;
  let hasPreorderItems = false;

  for (const item of preorderItems) {
    if (item.total > 0) {
      hasPreorderItems = true;
      preorderTotal += item.total;
    }
  }

  const serviceChargeTotal = getBookingServiceChargeTotal(booking);

  if (hasPreorderItems || serviceChargeTotal > 0) return true;

  const paidDepositsTotal = getBookingDepositTotal(booking);
  const depositDifference =
    paidDepositsTotal - (preorderTotal + serviceChargeTotal);
  if (depositDifference > 0) return true;

  // When deposits were already invoiced and then fully refunded,
  // the refunds should appear as separate items on the invoice
  return shouldProcessRefundsAsItems(booking);
};

/**
 * Check whether all deposits on the booking have tax rate assignments.
 * Checks deposit-level tax_rate_ids first, then falls back to the
 * "deposits" type's assignment-level rates (matching the API's fallback behaviour).
 *
 * Matches collins-ui's depositsHaveRates.
 */
export const depositsHaveRates = (
  booking: BookingV4,
  invoiceTaxConfig?: InvoiceTaxConfig,
  typeTaxAssignments?: TypeTaxAssignment[],
  typesCache?: Array<{ id: string; name: string }>
): boolean => {
  const deposits = booking.deposits;
  if (!Array.isArray(deposits) || deposits.length === 0) return true;

  // Find the "deposits" type ID
  const depositsType = typesCache?.find(
    (t) => typeof t.name === "string" && t.name.toLowerCase() === "deposits"
  );
  const depositsTypeId = depositsType?.id ?? null;

  return deposits.every((deposit) => {
    const ids = deposit.tax_rate_ids;
    if (Array.isArray(ids) && ids.length > 0) return true;

    // Fall back to the "deposits" type's assignment-level rates
    if (depositsTypeId) {
      const rates = getTaxRatesForType(
        depositsTypeId,
        invoiceTaxConfig,
        typeTaxAssignments
      );
      return Array.isArray(rates) && rates.length > 0;
    }

    return false;
  });
};

// ---------------------------------------------------------------------------
// Legacy fallback helpers for non-migrated venues
// ---------------------------------------------------------------------------

/**
 * Determine whether a venue has been migrated to the new tax system.
 * A venue is considered migrated when it has typeTaxAssignments, typesCache,
 * and a tax_region_id configured. Non-migrated venues use legacy fallback
 * logic for deposit, preorder, and refund invoice line items.
 */
export const isVenueMigrated = (
  typeTaxAssignments?: TypeTaxAssignment[],
  typesCache?: Array<{ id: string; name: string }>,
  taxRegionId?: string | null
): boolean => {
  const hasAssignments =
    Array.isArray(typeTaxAssignments) && typeTaxAssignments.length > 0;
  const hasTypesCache = Array.isArray(typesCache) && typesCache.length > 0;
  const hasTaxRegion = !!taxRegionId;

  return hasAssignments && hasTypesCache && hasTaxRegion;
};

/**
 * Legacy deposit handling for non-migrated venues.
 * Filters deposits by excluding `customer_preorder` and `request_auth` types,
 * skips preorder deduction logic, and uses `taxRates[0]` as the default rate.
 */
export const buildLegacyDepositItems = (
  booking: BookingV4,
  taxRates: TaxRate[],
  venueConfig?: {
    override_booking_invoicing?: boolean;
    booking_invoicing?: Record<string, string>;
  }
): InvoiceItemData[] => {
  const deposits = booking.deposits ?? [];
  if (deposits.length === 0) return [];

  const defaultRate = taxRates.length > 0 ? taxRates[0] : undefined;

  // Filter out customer_preorder and request_auth deposit types
  const filteredDeposits = deposits.filter((deposit) => {
    const depositType = deposit.type?.toString() ?? "";
    return (
      depositType !== "customer_preorder" && depositType !== "request_auth"
    );
  });

  if (filteredDeposits.length === 0) return [];

  // Sum all filtered deposits into a single amount
  let totalAmount = 0;
  for (const deposit of filteredDeposits) {
    const rawAmount = deposit.amount;
    const amount =
      typeof rawAmount === "number"
        ? rawAmount
        : ((rawAmount as { amount?: number })?.amount ?? 0);
    totalAmount += amount;
  }

  if (totalAmount <= 0) return [];

  const label = getInvoiceLabelForType("deposit_label", "Deposit", venueConfig);

  return [
    {
      uid: crypto.randomUUID(),
      description: label,
      gross_amount: roundToDecimal(totalAmount, 2),
      tax_rate: defaultRate,
      taxable: defaultRate !== undefined,
      tax_inclusive: true
    }
  ];
};

/**
 * Legacy preorder handling for non-migrated venues.
 * Skips individual-items mode entirely and falls back to simple
 * aggregated lines grouped by food/drink/other buckets.
 * Uses `taxRates[0]` as the default rate for all items.
 */
export const buildLegacyPreorderItems = (
  booking: BookingV4,
  taxRates: TaxRate[],
  venueConfig?: {
    override_booking_invoicing?: boolean;
    booking_invoicing?: Record<string, string>;
  }
): InvoiceItemData[] => {
  const preorderItems = getAllPreorderItems(booking);
  if (preorderItems.length === 0) return [];

  const defaultRate = taxRates.length > 0 ? taxRates[0] : undefined;

  // Aggregate items into food/drink/other buckets
  const buckets: Record<string, { amount: number; label: string }> = {
    food: { amount: 0, label: "Pre-ordered Food" },
    drink: { amount: 0, label: "Pre-ordered Drinks" },
    other: { amount: 0, label: "Other pre-ordered items" }
  };

  for (const item of preorderItems) {
    const itemType = item.type || "other";
    const bucket = buckets[itemType] ?? buckets.other;
    bucket.amount += item.total ?? 0;
  }

  const newItems: InvoiceItemData[] = [];

  for (const bucket of Object.values(buckets)) {
    if (bucket.amount <= 0) continue;

    const label = getInvoiceLabelForType(
      "preorder_label",
      bucket.label,
      venueConfig
    );

    newItems.push({
      uid: crypto.randomUUID(),
      description: label,
      gross_amount: roundToDecimal(bucket.amount, 2),
      taxable: defaultRate !== undefined,
      tax_rate: defaultRate,
      tax_inclusive: true
    });
  }

  // Add service charge if applicable
  const serviceChargeTotal = getActivePreorders(booking).reduce(
    (total, preorder) => total + (preorder.service_charge_amount ?? 0),
    0
  );

  if (serviceChargeTotal > 0) {
    const serviceChargeLabel = getInvoiceLabelForType(
      "service_charge_label",
      "Service charge",
      venueConfig
    );

    newItems.push({
      uid: crypto.randomUUID(),
      description: serviceChargeLabel,
      gross_amount: roundToDecimal(serviceChargeTotal, 2),
      taxable: false,
      tax_rate: defaultRate
    });
  }

  return newItems;
};

/**
 * Legacy refund handling for non-migrated venues.
 * Sums all refunds into a single negative line item using the
 * deposits' default label and first available tax rate.
 */
export const buildLegacyRefundItems = (
  booking: BookingV4,
  taxRates: TaxRate[],
  venueConfig?: {
    override_booking_invoicing?: boolean;
    booking_invoicing?: Record<string, string>;
  }
): InvoiceItemData[] => {
  const refunds = booking.refunds ?? [];
  if (refunds.length === 0) return [];

  const defaultRate = taxRates.length > 0 ? taxRates[0] : undefined;

  let totalRefundAmount = 0;
  for (const refund of refunds) {
    const rawAmount =
      typeof refund.amount === "number"
        ? refund.amount
        : ((refund.amount as { amount?: number })?.amount ?? 0);
    totalRefundAmount += rawAmount;
  }

  if (totalRefundAmount <= 0) return [];

  const label = getInvoiceLabelForType("refund_label", "Refund", venueConfig);

  return [
    {
      uid: crypto.randomUUID(),
      description: label,
      gross_amount: -Math.abs(roundToDecimal(totalRefundAmount, 2)),
      tax_rate: defaultRate,
      taxable: defaultRate !== undefined,
      tax_inclusive: true
    }
  ];
};

// ---------------------------------------------------------------------------
// Shared helpers for InvoicesSection handlers
// ---------------------------------------------------------------------------

/**
 * Resolved fallback tax configuration for the "deposits" type.
 * Used by both deposit and refund handlers as a tier-2 fallback.
 */
export interface FallbackTaxConfig {
  fallbackTaxRates: TaxRate[];
  fallbackHasTaxAssignment: boolean;
  fallbackTaxRate: TaxRate | undefined;
  fallbackTaxInclusive: boolean;
}

/**
 * Resolve the fallback tax configuration for the deposits type.
 * Shared between handleAddRefundItems and handleAddDepositItems.
 */
export const resolveFallbackTaxConfig = (config: {
  depositsTypeId: string | null | undefined;
  invoiceTaxConfig?: InvoiceTaxConfig;
  typeTaxAssignments?: TypeTaxAssignment[];
  taxRates: TaxRate[];
  taxLocking: boolean;
}): FallbackTaxConfig => {
  const {
    depositsTypeId,
    invoiceTaxConfig,
    typeTaxAssignments,
    taxRates,
    taxLocking
  } = config;
  const fallbackTaxRates = depositsTypeId
    ? getTaxRatesForType(depositsTypeId, invoiceTaxConfig, typeTaxAssignments)
    : [];
  const fallbackHasTaxAssignment =
    Array.isArray(fallbackTaxRates) && fallbackTaxRates.length > 0;
  const fallbackTaxRate = fallbackHasTaxAssignment
    ? createCombinedTaxRate(fallbackTaxRates, taxLocking)
    : taxRates[0];
  const fallbackTaxInclusive =
    fallbackHasTaxAssignment && depositsTypeId
      ? getTaxInclusiveForType(
          depositsTypeId,
          invoiceTaxConfig,
          typeTaxAssignments
        )
      : true;

  return {
    fallbackTaxRates,
    fallbackHasTaxAssignment,
    fallbackTaxRate,
    fallbackTaxInclusive
  };
};

/**
 * Build an invoice item using 3-tier tax resolution:
 * Tier 1: deposit's own tax rates, Tier 2: fallback type assignment, Tier 3: no tax.
 */
export const buildTieredTaxInvoiceItem = (config: {
  description: string;
  amount: number;
  matchingRates: TaxRate[];
  fallbackConfig: FallbackTaxConfig;
  taxRates: TaxRate[];
  taxLocking: boolean;
}): InvoiceItemData => {
  const {
    description,
    amount,
    matchingRates,
    fallbackConfig,
    taxRates,
    taxLocking
  } = config;
  const {
    fallbackTaxRates,
    fallbackHasTaxAssignment,
    fallbackTaxRate,
    fallbackTaxInclusive
  } = fallbackConfig;

  if (matchingRates.length > 0) {
    const combinedRate = createCombinedTaxRate(matchingRates, taxLocking);
    return {
      uid: crypto.randomUUID(),
      description,
      taxable: true,
      tax_rate: combinedRate,
      tax_rates: matchingRates.length > 1 ? matchingRates : undefined,
      multiple_rates: matchingRates.length > 1,
      locked_tax: true,
      tax_inclusive: true,
      gross_amount: amount
    };
  }

  if (fallbackHasTaxAssignment) {
    return {
      uid: crypto.randomUUID(),
      description,
      taxable: true,
      tax_rate: fallbackTaxRate,
      tax_rates: fallbackTaxRates.length > 1 ? fallbackTaxRates : undefined,
      multiple_rates: fallbackTaxRates.length > 1,
      locked_tax: taxLocking && fallbackTaxRates.length > 0,
      tax_inclusive: fallbackTaxInclusive,
      ...(fallbackTaxInclusive
        ? { gross_amount: amount }
        : { net_amount: amount })
    };
  }

  return {
    uid: crypto.randomUUID(),
    description,
    gross_amount: amount,
    tax_amount: 0,
    taxable: false,
    tax_rate: taxRates[0]
  };
};

/**
 * Collect regular items and footer for a type group starting from an index.
 * Used by mergePreorderItemsIntoExisting.
 */
const collectTypeGroupItems = (
  preorderItems: InvoiceItemData[],
  startIndex: number,
  typeId: string
): {
  regularItems: InvoiceItemData[];
  footer?: InvoiceItemData;
  nextIndex: number;
} => {
  const regularItems: InvoiceItemData[] = [];
  let footer: InvoiceItemData | undefined;
  let i = startIndex;

  while (i < preorderItems.length) {
    const next = preorderItems[i];
    if (next.type === "footer" && next.type_id === typeId) {
      footer = next;
      i++;
      break;
    }
    if (next.type_id === typeId) {
      regularItems.push(next);
    } else {
      break;
    }
    i++;
  }

  return { regularItems, footer, nextIndex: i };
};

/**
 * Insert new items before an existing footer and update the footer subtotal.
 * Used by mergePreorderItemsIntoExisting.
 */
const insertItemsIntoExistingGroup = (
  existingItems: InvoiceItemData[],
  footerIndex: number,
  regularItems: InvoiceItemData[],
  footer?: InvoiceItemData
): void => {
  let insertAt = footerIndex;
  for (const regularItem of regularItems) {
    existingItems.splice(insertAt, 0, regularItem);
    insertAt++;
  }
  if (!footer) return;

  const updatedFooter = { ...existingItems[insertAt] };
  if (footer.gross_amount !== undefined) {
    updatedFooter.gross_amount = footer.gross_amount;
  }
  if (footer.net_amount !== undefined) {
    updatedFooter.net_amount = footer.net_amount;
  }
  updatedFooter.tax_inclusive = footer.tax_inclusive;
  existingItems[insertAt] = updatedFooter;
};

/**
 * Merge new preorder items into existing invoice items.
 * For type groups that already exist, inserts items before the footer
 * and updates the footer subtotal. Otherwise appends.
 */
export const mergePreorderItemsIntoExisting = (
  existingItems: InvoiceItemData[],
  preorderItems: InvoiceItemData[]
): InvoiceItemData[] => {
  const result = [...existingItems];
  const itemsToAppend: InvoiceItemData[] = [];

  let i = 0;
  while (i < preorderItems.length) {
    const item = preorderItems[i];

    if (item.type !== "heading" || !item.type_id) {
      itemsToAppend.push(item);
      i++;
      continue;
    }

    const typeId = item.type_id;
    const { regularItems, footer, nextIndex } = collectTypeGroupItems(
      preorderItems,
      i + 1,
      typeId
    );
    i = nextIndex;

    const { headerIndex, footerIndex } = findExistingHeaderAndFooterIndices(
      result,
      typeId
    );

    if (headerIndex !== -1 && footerIndex !== -1) {
      insertItemsIntoExistingGroup(result, footerIndex, regularItems, footer);
    } else {
      itemsToAppend.push(item, ...regularItems);
      if (footer) itemsToAppend.push(footer);
    }
  }

  return [...result, ...itemsToAppend];
};

/**
 * Deep equality comparison for two values.
 * Handles primitives, null/undefined, arrays, dates and plain objects.
 * Used for dirty-tracking invoice changes instead of JSON.stringify which
 * is fragile (property ordering) and inefficient for large objects.
 */
export const isDeepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;

  if (a === null || b === null || a === undefined || b === undefined) {
    return false;
  }

  if (typeof a !== typeof b) return false;

  if (typeof a !== "object") return false;

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  if (Array.isArray(a)) {
    if (!Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    return a.every((item, index) => isDeepEqual(item, b[index]));
  }

  if (Array.isArray(b)) return false;

  const objA = a as Record<string, unknown>;
  const objB = b as Record<string, unknown>;
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) return false;

  return keysA.every(
    (key) => Object.hasOwn(objB, key) && isDeepEqual(objA[key], objB[key])
  );
};
